import { Karti } from './Karti';
import type { ActivityData } from '../../lib/api/statistics';

const LEVEL_COLORS = ['#2B2925', '#6B5E2A', '#B8992A', '#F4D03F'];

function level(count: number) {
  if (count <= 0) return 0;
  if (count < 10) return 1;
  if (count < 30) return 2;
  return 3;
}

const MONTHS = ['JAN', 'FEB', 'MÄR', 'APR', 'MAI', 'JUN', 'JUL', 'AUG', 'SEP', 'OKT', 'NOV', 'DEZ'];

/** Ink streak panel with a 15x7 activity heatmap (newest bottom-right). */
export function StreakPanel({ activity }: { activity: ActivityData }) {
  const COLS = 15;
  const days = activity.days.slice(-COLS * 7);
  const pad = COLS * 7 - days.length;
  const cells: ({ date: string; count: number } | null)[] = [...Array(pad).fill(null), ...days];

  const monthLabels = Array.from({ length: COLS }, (_, c) => {
    const first = cells.slice(c * 7, c * 7 + 7).find(Boolean);
    if (!first) return '';
    const m = Number(first.date.slice(5, 7)) - 1;
    const prev = c > 0 ? cells.slice((c - 1) * 7, c * 7).find(Boolean) : null;
    const pm = prev ? Number(prev.date.slice(5, 7)) - 1 : -1;
    return m !== pm ? MONTHS[m] : '';
  });

  return (
    <div className="space-y-3.5 rounded-hero bg-ink px-4 pb-3.5 pt-4 text-paper">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Karti size={44} mood={activity.learnedToday ? 'happy' : activity.due.total > 0 ? 'nag' : 'sleep'} />
          <div className="flex items-baseline gap-3">
            <span className="text-[56px] font-extrabold leading-none tracking-tight text-marker">
              {activity.streak}
            </span>
            <span className="text-base font-bold">Tage am Stück</span>
          </div>
        </div>
        <span className="pt-1 font-mono text-[11px] font-bold tracking-[0.07em] text-on-ink-sub">
          REKORD {activity.record}
        </span>
      </div>

      <div>
        <div
          className="grid grid-flow-col gap-[3px]"
          style={{ gridTemplateRows: 'repeat(7, 1fr)', gridTemplateColumns: `repeat(${COLS}, 1fr)` }}
        >
          {cells.map((cell, i) => {
            const isToday = cell?.date === activity.today;
            return (
              <div
                key={i}
                title={cell ? `${new Date(cell.date).toLocaleDateString('de-DE')}: ${cell.count}` : undefined}
                className="aspect-square animate-cellpop rounded-[3px]"
                style={{
                  background: cell ? LEVEL_COLORS[level(cell.count)] : 'transparent',
                  animationDelay: `${Math.floor(i / 7) * 35}ms`,
                  outline: isToday ? '2px solid #F3EFE6' : undefined,
                  outlineOffset: isToday ? 1 : undefined,
                }}
              />
            );
          })}
        </div>
        <div
          className="mt-2 grid font-mono text-[10px] font-bold text-on-ink-sub"
          style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)` }}
        >
          {monthLabels.map((l, i) => (
            <span key={i} className="overflow-visible whitespace-nowrap">
              {l}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
